import { createFileRoute, useRouter } from '@tanstack/react-router';
import { z } from 'zod';
import { ArrowLeft } from '@/core/ui/icons';
import { cn, Page } from '@/core/ui';
import { PersonActivity } from '@/modules/audit/public';
import { UserReportView } from '@/modules/learning/public';
import { RequirePermission, usePermissions } from '@/modules/identity/public';

type PersonTab = 'aulas' | 'atividade';

/**
 * Uma pessoa: o que assistiu (audiência) e o que fez no portal (atividade).
 * Cada aba aparece para quem tem a permissão dela.
 */
export const Route = createFileRoute('/_admin/pessoas/$userId')({
  validateSearch: z.object({
    aba: z.enum(['aulas', 'atividade']).optional().catch(undefined),
  }),
  component: function PersonRoute() {
    const { userId } = Route.useParams();
    const { aba } = Route.useSearch();
    const navigate = Route.useNavigate();
    const router = useRouter();
    const { can } = usePermissions();
    const tabs: { id: PersonTab; label: string }[] = [
      ...(can('audiencia.ver') ? [{ id: 'aulas' as const, label: 'Aulas' }] : []),
      ...(can('auditoria.ver') ? [{ id: 'atividade' as const, label: 'Atividade' }] : []),
    ];
    const tab = tabs.find((t) => t.id === aba)?.id ?? tabs[0]?.id;

    return (
      <RequirePermission anyOf={['audiencia.ver', 'auditoria.ver']}>
        <Page>
          <button
            type="button"
            onClick={() => router.history.back()}
            className="mb-5 inline-flex items-center gap-1.5 text-sm text-fg-subtle hover:text-fg"
          >
            <ArrowLeft aria-hidden className="size-4" />
            Voltar
          </button>
          {tabs.length > 1 && (
            <div role="tablist" aria-label="Pessoa" className="mb-6 flex gap-6 border-b border-line">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => void navigate({ search: { aba: t.id }, replace: true })}
                  className={cn(
                    '-mb-px border-b-2 pb-2.5 text-sm font-medium whitespace-nowrap transition-colors',
                    tab === t.id
                      ? 'border-brand-400 text-fg'
                      : 'border-transparent text-fg-subtle hover:text-fg',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          {tab === 'aulas' && <UserReportView userId={userId} />}
          {tab === 'atividade' && <PersonActivity userId={userId} />}
        </Page>
      </RequirePermission>
    );
  },
});
